import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Table from 'react-bootstrap/Table';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as ChartTooltipPrimitive, ResponsiveContainer, Cell,
} from 'recharts';
import httpService from '../../../services/httpService';
import { TabSpinner, Th } from './shared';

const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-white border rounded px-3 py-2 shadow-sm" style={{ fontSize: 13 }}>
            <div className="fw-semibold mb-1">{payload[0].payload.fullName}</div>
            <div>{payload[0].value} interaction{payload[0].value !== 1 ? 's' : ''}</div>
        </div>
    );
};

const InteractionsTab = ({ clubGuid }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        httpService.get(`/dash/podcast-club/${clubGuid}/statistics/interactions`)
            .then((res) => setData(res.data))
            .catch((err) => setError(err.response?.data?.message || 'Error loading data'))
            .finally(() => setLoading(false));
    }, [clubGuid]);

    if (loading) return <TabSpinner />;
    if (error) return <Alert variant="danger">{error}</Alert>;
    if (!data) return null;

    const chartData = (data.interactions_chart || []).map((ep) => ({
        name: ep.key?.length > 22 ? ep.key.substring(0, 22) + '…' : ep.key,
        fullName: ep.key,
        value: ep.value,
        breakdown: ep.breakdown,
    }));

    const completionRows = (data.interactions_chart || []).filter((ep) => ep.breakdown?.practices > 0);

    return (
        <div>
            <p className="text-muted small mb-1">One bar per episode, ordered by publish date.</p>
            <p className="text-muted small mb-3">
                <strong>Bar height</strong> = practices recorded + practice comments + discussion comments + reactions for that episode. All four signal types are weighted equally.
            </p>

            {chartData.length === 0 ? (
                <p className="text-muted small">No interaction data yet.</p>
            ) : (
                <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 50 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <ChartTooltipPrimitive content={<ChartTooltip />} />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                            {chartData.map((_, i) => (
                                <Cell key={i} fill={i % 2 === 0 ? '#FACE68' : '#5A9CB5'} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            )}

            {completionRows.length > 0 && (
                <div className="mt-4">
                    <h6 className="fw-semibold mb-1">Practice completion by episode</h6>
                    <p className="text-muted small mb-3">
                        Of the practices listeners created per episode, how many did they follow through and complete?
                    </p>
                    <Table hover responsive size="sm" className="align-middle">
                        <thead className="table-light">
                            <tr>
                                <Th tip="The episode title.">Episode</Th>
                                <Th className="text-end" tip="Total practices created by all listeners for this episode.">Practices</Th>
                                <Th className="text-end" tip="Practices marked as completed.">Completed</Th>
                                <Th tip="Completed ÷ total practices. A higher rate means listeners are acting on this episode's insights.">Completion rate</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {completionRows.map((ep, i) => {
                                const rate = ep.breakdown?.completion_rate ?? 0;
                                const barColor = rate >= 70 ? '#198754' : rate >= 40 ? '#ffc107' : '#dc3545';
                                return (
                                    <tr key={i}>
                                        <td className="fw-medium">{ep.key}</td>
                                        <td className="text-end">{ep.breakdown?.practices ?? 0}</td>
                                        <td className="text-end">{ep.breakdown?.practices_completed ?? 0}</td>
                                        <td style={{ minWidth: 140 }}>
                                            <div className="d-flex align-items-center gap-2">
                                                <div style={{ flex: 1, height: 6, background: '#e9ecef', borderRadius: 3, overflow: 'hidden' }}>
                                                    <div style={{ width: `${rate}%`, height: '100%', background: barColor, borderRadius: 3 }} />
                                                </div>
                                                <span className="small text-muted" style={{ width: 36, textAlign: 'right' }}>{rate}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            )}
        </div>
    );
};

export default InteractionsTab;
